/**
 * Service layer — Construction · Bid Campaigns (Supabase-backed)
 *
 * Wraps /api/bid-campaigns (Clerk-authed, service_role). Supabase is the source of
 * truth for bid campaigns; Organizations (bidders / GCs) still reference Airtable
 * record ids with a denormalized name snapshot. Components call only these functions
 * — never supabase.from(...) directly.
 */

async function call(method, body, qs) {
  const token = await window.__clerkGetToken()
  const url = '/api/bid-campaigns' + (qs ? '?' + qs : '')
  const res = await fetch(url, {
    method,
    headers: method === 'GET'
      ? { 'Authorization': 'Bearer ' + token }
      : { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: method === 'GET' ? undefined : JSON.stringify(body),
  })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    const err = new Error(b.error || 'Bid campaign request failed: ' + res.status)
    // Carry the structured payload through: a 409 from the one-active-contract-per-project
    // invariant (database/209) names the incumbent contract, and the caller needs it to offer
    // "make this the active one" rather than showing a bare error code.
    err.code = b.error || null
    err.conflict = b.conflict || null
    throw err
  }
  return res.json()
}

/** Thrown-error code for the one-active-contract-per-project conflict (database/209). */
export const ACTIVE_CONTRACT_CONFLICT = 'active_contract_exists'

/* ── Reads ────────────────────────────────────────────────────────────────── */

/** All bid campaigns for a project (each includes any awarded contract). */
export function getProjectCampaigns(projectId) {
  if (!projectId) return Promise.resolve([])
  return call('GET', null, 'projectId=' + encodeURIComponent(projectId)).then(d => d.campaigns || [])
}

/** Full detail for one campaign: { campaign, bidders, bids, questions, contract }. */
export function getCampaignDetail(campaignId) {
  return call('GET', null, 'campaignId=' + encodeURIComponent(campaignId))
}

/** A bid plus its line items: { bid, lineItems }. */
export function getBidDetail(bidId) {
  return call('GET', null, 'bidId=' + encodeURIComponent(bidId))
}

/* ── Campaign writes ──────────────────────────────────────────────────────── */

export function createCampaign(projectId, fields) {
  return call('POST', { action: 'createCampaign', projectId, fields }).then(d => d.campaign)
}

export function updateCampaign(campaignId, fields) {
  return call('POST', { action: 'updateCampaign', campaignId, fields }).then(d => d.ok)
}

export function deleteCampaign(campaignId) {
  return call('POST', { action: 'deleteCampaign', campaignId }).then(d => d.ok)
}

/** Link a campaign to a milestone (creates the bid_campaign_milestones row). */
export function linkMilestone(campaignId, milestoneAirtableId, role = 'primary') {
  return call('POST', { action: 'linkMilestone', campaignId, milestoneAirtableId, role }).then(d => d.ok)
}

/** Unlink a campaign from a milestone. */
export function unlinkMilestone(campaignId, milestoneAirtableId) {
  return call('POST', { action: 'unlinkMilestone', campaignId, milestoneAirtableId }).then(d => d.ok)
}

/**
 * Get all bid campaign details mounted at a specific milestone.
 * Returns { details: [{campaign, bids, questions, contract, bidders}] }
 */
export function getBidCampaignForMilestone(milestoneAirtableId) {
  return call('POST', { action: 'getByCampaignMilestone', milestoneAirtableId }).then(d => d.details || [])
}

/** Replace the campaign's bidder set. bidders: [{ orgId, orgName, role: 'invited'|'notify' }]. */
export function setBidders(campaignId, bidders) {
  return call('POST', { action: 'setBidders', campaignId, bidders }).then(d => d.ok)
}

/* ── Construction Contracts ───────────────────────────────────────────────── */

export const CONTRACT_STATUS_LABELS = {
  active:    'Active',
  closed:    'Closed Out',
  on_hold:   'On Hold',
}

/** All construction contracts for a project (awarded + imported). */
export function getProjectContracts(projectId) {
  if (!projectId) return Promise.resolve([])
  return call('GET', null, 'contractsForProject=' + encodeURIComponent(projectId)).then(d => d.contracts || [])
}

/** One contract + its linked RFI count: { contract, rfiCount }. */
export function getContract(contractId) {
  return call('GET', null, 'contractId=' + encodeURIComponent(contractId))
}

/* `makeActive` supersedes the project's existing active contract (closing it out) instead of
   failing with ACTIVE_CONTRACT_CONFLICT — pass it only after the user has confirmed. */
export function createContract(fields, { makeActive = false } = {}) {
  return call('POST', { action: 'createContract', fields, makeActive }).then(d => d.contract)
}
export function updateContract(contractId, fields, { makeActive = false } = {}) {
  return call('POST', { action: 'updateContract', contractId, fields, makeActive }).then(d => d.ok)
}
export function deleteContract(contractId) {
  return call('POST', { action: 'deleteContract', contractId }).then(d => d.ok)
}

/* ── Bid writes ───────────────────────────────────────────────────────────── */

export function createBid(campaignId, fields) {
  return call('POST', { action: 'createBid', campaignId, fields }).then(d => d.bid)
}

export function updateBid(bidId, fields) {
  return call('POST', { action: 'updateBid', bidId, fields }).then(d => d.ok)
}

export function deleteBid(bidId) {
  return call('POST', { action: 'deleteBid', bidId }).then(d => d.ok)
}

/** Replace a bid's line items. items: [{ name, gcTitle, sectionCode, division, amount }]. */
export function setLineItems(bidId, items) {
  return call('POST', { action: 'setLineItems', bidId, items }).then(d => d.ok)
}

/* ── Questions ────────────────────────────────────────────────────────────── */

export function createQuestion(campaignId, fields) {
  return call('POST', { action: 'createQuestion', campaignId, fields }).then(d => d.question)
}

export function answerQuestion(questionId, answer) {
  return call('POST', { action: 'answerQuestion', questionId, answer }).then(d => d.ok)
}

export function deleteQuestion(questionId) {
  return call('POST', { action: 'deleteQuestion', questionId }).then(d => d.ok)
}

/* ── Award ────────────────────────────────────────────────────────────────── */

/**
 * Award the campaign — create/update its construction contract and close the campaign.
 * fields: { project_id, contractor_airtable_id, contractor_name, contract_type,
 *           contract_value, contract_start_date, winning_bid_id }
 */
export function awardCampaign(campaignId, fields, { makeActive = false } = {}) {
  return call('POST', { action: 'award', campaignId, fields, makeActive }).then(d => d.contract)
}

/* ── Display helpers ──────────────────────────────────────────────────────── */

export const CONTRACTING_METHODS = ['Lump Sum', 'Cost Plus']

export const CONTRACT_TYPES = [
  'Lump Sum', 'Cost-Plus', 'Cost-Plus with GMP', 'Time and Materials',
  'Construction Manager at Risk', 'Contractors Fee', 'Design-Build',
]

export const CAMPAIGN_STATUS_LABELS = {
  preview: 'Preview - Coming Soon',
  open:    'Bidding In Progress',
  closed:  'Closed - Bidding Complete',
}

/** Sum of a bid's normalization deltas (for showing the leveling adjustment). */
export function bidAdjustment(bid) {
  if (!bid) return 0
  return (
    Number(bid.allowance_baseline_delta || 0) +
    Number(bid.alternate_selection_delta || 0) +
    Number(bid.excluded_scope_delta || 0) +
    Number(bid.bidder_included_extra_deduct || 0) +
    Number(bid.tax_permits_delta || 0) +
    Number(bid.gc_bonding_delta || 0)
  )
}
